<template>
  <d-card class="p-4 order-collect-boxes">
    <h4 :class="{
      'mb-2': true,
      'd-flex': true
    }">
      <span class="d-block mr-auto">
        {{ $t('order.warehouse.boxes.title') }}
      </span>

      <template v-if="mode !== 'order'">
        <d-button
          v-if="!showBoxInitButton && loadedBoxes && !readOnly"
          theme="success"
          @click.prevent="saveBoxes()"
          class="ml-auto"
          :disabled="saveInProgress">
          <div
            v-if="saveInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          {{ $t('order.warehouse.boxes.saveBoxes') }}
        </d-button>

        <d-button
          v-if="!showBoxInitButton && loadedBoxes && readOnly && hasSavedBoxes"
          theme="danger"
          @click.prevent="cancelBoxes()"
          class="ml-2"
          :disabled="saveInProgress || cancelInProgress">
          <div
            v-if="cancelInProgress"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          {{ $t('order.warehouse.boxes.cancelBoxes') }}
        </d-button>

        <d-button
          v-if="readOnly"
          theme="primary"
          @click.prevent="downloadLabel('all')"
          class="ml-2"
          :disabled="!!downloadingLabel">
          <div
            v-if="downloadingLabel === 'all'"
            class="spinner-border spinner-border-sm"
            role="status"></div>
          {{ $t('order.warehouse.boxes.downloadAllLabels') }}
        </d-button>
      </template>
    </h4>

    <span class="d-block">
      {{ $t('order.warehouse.boxes.deliveryNoteConfirmedAt') }}:
      <span
        v-if="deliveryNoteConfirmedAt"
        class="text-primary">
        {{ deliveryNoteConfirmedAt }}
      </span>
      <span v-else>
        &mdash;
      </span>
    </span>

    <span
      v-if="deliveryNoteConfirmedBy"
      class="d-block">
      {{ $t('order.warehouse.boxes.deliveryNoteConfirmedBy') }}:
      <span class="text-primary">
        {{ deliveryNoteConfirmedBy.name }}
      </span>
    </span>

    <div :class="{
      'is-loading': saveInProgress,
      'mt-3': true
    }">
      <div
        v-if="showBoxInitButton && loadedBoxes && mode !== 'order'"
        class="text-center mt-4 mb-4">
        <h5 class="mb-4 text-light">
          {{ $t('order.warehouse.boxes.productsAreNotDividedIntoBoxesYet') }}
        </h5>

        <d-button
          theme="success"
          @click.prevent="generateBoxes">
          {{ $t('order.warehouse.boxes.generateBoxes') }}
        </d-button>
      </div>

      <div v-if="!showBoxInitButton && loadedBoxes">
        <validation-observer ref="boxes-divide-form" tag="d-row">
          <d-col cols="6">
            <div
              v-if="!readOnly && mode !== 'order'"
              class="d-flex">
              <d-button
                theme="success"
                @click.prevent="addBox()"
                class="ml-auto">
                {{ $t('order.warehouse.boxes.addBox') }}
              </d-button>

              <d-button
                theme="warning"
                @click.prevent="resetBoxes()"
                class="ml-2 mr-auto">
                {{ $t('order.warehouse.boxes.resetBoxes') }}
              </d-button>
            </div>

            <d-card
              v-for="(box, index) of boxes"
              :key="'box-' + index"
              class="mb-3 mt-3"
              :style="box.embroidery_received_comment ? 'border: 2px solid red;' : ''">
              <d-card-body
                :border-theme="boxSpaceUsage[index] * 100 > 100 ? 'danger' : 'normal'"
                class="px-3 pt-3 pb-2">
                <d-row v-if="!readOnly">
                  <d-col cols="6">
                    <small>
                      {{ $t('order.warehouse.boxes.cheatsheet.productName') }}
                    </small>
                  </d-col>
                  <d-col cols="2">
                    <small>
                      {{ $t('order.warehouse.boxes.cheatsheet.piecesInCarton') }}
                    </small>
                  </d-col>
                  <d-col cols="4" class="pl-4">
                    <small>
                      {{ $t('order.warehouse.boxes.cheatsheet.includingSupplier') }}
                    </small>
                  </d-col>
                </d-row>
                <d-row
                  v-for="(product, subindex) of box.products"
                  :key="'box-' + index +'-item-' + subindex"
                  class="mb-2">
                  <div
                    v-if="readOnly"
                    :id="'product-item-in-box-wrapper-' + index + '-' + subindex"
                    :class="{
                      'px-3 py-1': true,
                      'is-invalid': orderProductIDs.indexOf(product.product_id) === -1,
                      'w-100': true
                    }">
                    <strong class="text-primary d-flex align-middle">
                      <i
                        v-if="orderProductIDs.indexOf(product.product_id) === -1"
                        :id="'product-item-in-box-' + index + '-' + subindex"
                        class="material-icons text-danger">
                        error
                      </i>

                      <span class="pl-1">{{ product.product_code }} - {{ product.product_name }}</span>

                      <d-badge
                        v-if="product.out_of_stock"
                        theme="danger">
                        {{ $t('order.warehouse.boxes.outOfStock') }}
                      </d-badge>

                      <d-tooltip
                        v-if="orderProductIDs.indexOf(product.product_id) === -1"
                        :target="'#product-item-in-box-' + index + '-' + subindex"
                        :container="'#product-item-in-box-wrapper-' + index + '-' + subindex">
                        🛑 {{ $t('order.warehouse.boxes.productNotExistsInOrder') }}
                      </d-tooltip>

                      <d-button
                        v-if="product.order_product_deleted && product.returned_to_stock_at"
                        :id="'remove-box-product-button-' + index + '-' + subindex"
                        theme="danger"
                        :outline="true"
                        class="ml-auto"
                        @click.prevent.stop="returnToStockProductFromBox(index, subindex, product.returned_to_stock_at)">
                        <div
                          v-if="removeInProgress === index + '-' + subindex"
                          class="spinner-border spinner-border-sm"
                          role="status"></div>
                        <i
                          v-else
                          class="material-icons">delete</i>
                      </d-button>
                    </strong>
                    <div class="product-stats">
                      <small>
                        {{ $t('order.warehouse.boxes.inBox') }}:
                      </small>
                      <small>
                        {{ product.box_quantity }} {{ $t('ui.piecesAbbr') }}
                      </small>
                      <small></small>
                      <small>
                        {{ $t('order.warehouse.boxes.cheatsheet.fromSupplier') }}:
                      </small>
                      <small>
                        {{ product.purchase_quantity }} {{ $t('ui.piecesAbbr') }}
                      </small>
                      <small></small>
                      <small>
                        {{ $t('order.warehouse.boxes.collected') }}:
                      </small>
                      <small :class="{
                        'text-success': product.box_quantity === product.collected,
                        'text-danger': product.box_quantity !== product.collected
                      }">
                        {{ product.collected }} {{ $t('ui.piecesAbbr') }}
                      </small>
                      <small>
                        <a
                          href="#"
                          @click.prevent="showHistoryPopup('collections', product.collections)"
                          class="ml-5">
                          {{ $t('order.warehouse.boxes.collectHistory') }} ({{ product.collections.length }})
                        </a>
                      </small>
                      <small>
                        {{ $t('order.warehouse.boxes.verified') }}:
                      </small>
                      <small :class="{
                        'text-success': product.box_quantity === product.verified,
                        'text-danger': product.box_quantity !== product.verified
                      }">
                        {{ product.verified }} {{ $t('ui.piecesAbbr') }}
                      </small>
                      <small>
                        <a
                          href="#"
                          @click.prevent="showHistoryPopup('verifications', product.verifications)"
                          class="ml-5">
                          {{ $t('order.warehouse.boxes.verificationHistory') }} ({{ product.verifications.length }})
                        </a>
                      </small>
                      <template v-if="product.returned">
                        <small>
                          {{ $t('order.warehouse.boxes.returned') }}:
                        </small>
                        <small class="text-danger">
                          {{ product.returned }} {{ $t('ui.piecesAbbr') }}
                        </small>
                        <small>
                          <a
                            href="#"
                            @click.prevent="showHistoryPopup('returns', product.returns)"
                            class="ml-5">
                            {{ $t('order.warehouse.boxes.returnHistory') }} ({{ product.returns.length }})
                          </a>
                        </small>
                      </template>
                      <template v-if="product.return_to_stock && !product.returned_to_stock_at">
                        <small>
                          <strong class="text-danger">
                            {{ $t('order.warehouse.boxes.returnInProgress') }}
                          </strong>
                        </small>
                      </template>
                      <template v-if="product.returned_to_stock_at">
                        <small>
                          {{ $t('order.warehouse.boxes.returnedToStockAt') }}:
                        </small>
                        <small>
                          {{ product.returned_to_stock_at }}
                        </small>
                        <small></small>
                      </template>
                      <template v-if="product.returned_to_stock_by">
                        <small>
                          {{ $t('order.warehouse.boxes.returnedToStockBy') }}:
                        </small>
                        <small>
                          {{ product.returned_to_stock_by.name }}
                        </small>
                        <small></small>
                      </template>
                    </div>
                    <div v-if="product.collection_description">
                      <small>
                        {{ $t('order.warehouse.boxes.notes') }}:
                        <span class="text-primary">
                          {{ product.collection_description }}
                        </span>
                      </small>
                    </div>
                  </div>
                  <div
                    v-if="readOnly && (box.shipment_sent_at || box.user_sent_shipment_by || box.embroidery_sent_at || box.user_embroidery_sent_by || box.embroidery_received_at || box.user_embroidery_received_by || box.embroidery_received_comment)"
                    class="box-info px-3 w-100 my-2">
                    <small class="p-2">
                      <template v-if="box.shipment_sent_at">
                        <span class="w-50">
                          {{ $t('order.warehouse.boxes.info.shipmentSentAt') }}:
                        </span>
                        <span class="text-primary w-50">
                          {{ box.shipment_sent_at }}
                        </span>
                        <br>
                      </template>

                      <template v-if="box.user_sent_shipment_by">
                        <span class="w-50">
                          {{ $t('order.warehouse.boxes.info.shipmentSentBy') }}:
                        </span>
                        <span class="text-primary w-50">
                          {{ box.user_sent_shipment_by.name }}
                        </span>
                        <br>
                      </template>

                      <template v-if="box.embroidery_sent_at">
                        <span class="w-50">
                          {{ $t('order.warehouse.boxes.info.embroiderySentAt') }}:
                        </span>
                        <span class="text-primary w-50">
                          {{ box.embroidery_sent_at }}
                        </span>
                        <br>
                      </template>

                      <template v-if="box.user_embroidery_sent_by">
                        <span class="w-50">
                          {{ $t('order.warehouse.boxes.info.embroiderySentBy') }}:
                        </span>
                        <span class="text-primary w-50">
                          {{ box.user_embroidery_sent_by.name }}
                        </span>
                        <br>
                      </template>

                      <template v-if="box.embroidery_received_at">
                        <span class="w-50">
                          {{ $t('order.warehouse.boxes.info.embroideryReceivedAt') }}:
                        </span>
                        <span class="text-primary w-50">
                          {{ box.embroidery_received_at }}
                        </span>
                        <br>
                      </template>

                      <template v-if="box.user_embroidery_received_by">
                        <span class="w-50">
                          {{ $t('order.warehouse.boxes.info.embroideryReceivedBy') }}:
                        </span>
                        <span class="text-primary w-50">
                          {{ box.user_embroidery_received_by.name }}
                        </span>
                        <br>
                      </template>
                    </small>

                    <template v-if="box.embroidery_received_comment">
                      <small class="d-block py-1 px-2 mt-2 is-embroidery-comment">
                        {{ $t('order.warehouse.boxes.info.embroideryComment') }}:
                        <br>
                        <strong>
                          {{ box.embroidery_received_comment }}
                        </strong>
                      </small>
                    </template>
                  </div>
                  <template v-if="!readOnly">
                    <d-col cols="6">
                      <validation-provider
                        v-if="!readOnly && !product.order_product_deleted"
                        rules="required"
                        :name="'product-item-name-' + index + '-' + subindex"
                        class="w-75"
                        v-slot="{ errors }">
                        <d-form-select
                          v-model="product.order_product_id"
                          :state="errors.length ? 'invalid' : null"
                          :options="orderProductsList" />
                        <div class="invalid-feedback">
                          {{ errors[0] }}
                        </div>
                      </validation-provider>

                      <small v-if="product.order_product_deleted">
                        {{ product.product_name }}
                      </small>

                      <small
                        v-if="product.returned_to_stock_at"
                        class="text-danger d-block">
                        {{ $t('order.warehouse.boxes.returnedToStockAt') }}: {{ product.returned_to_stock_at }}
                        <template v-if="product.returned_to_stock_by">
                          {{ $t('order.warehouse.boxes.returnedToStockBy') }}: {{ product.returned_to_stock_by.name }}
                        </template>
                      </small>
                    </d-col>

                    <d-col
                      cols="6"
                      class="d-flex align-items-start pr-2">
                      <validation-provider
                        :rules="'required|min_value:1'"
                        :name="'product-item-quantity-' + index + '-' + subindex"
                        v-slot="{ errors }">
                        <d-input-group>
                          <input
                            type="number"
                            v-model="product.box_quantity"
                            min="1"
                            step="1"
                            :readonly="readOnly || !!product.order_product_deleted"
                            :class="{
                              'form-control': true,
                              'is-invalid': errors.length,
                              'text-right': true
                            }"
                            :invalid="errors.length ? true : null" />
                          <d-input-group-text slot="append">
                            {{ $t('ui.piecesAbbr') }}
                          </d-input-group-text>
                        </d-input-group>
                        <d-form-invalid-feedback>
                          {{ errors[0] }}
                        </d-form-invalid-feedback>
                      </validation-provider>

                      <validation-provider
                        :rules="'required|min_value:0|max_value:' + purchaseQuantities[product.order_product_id]"
                        :name="'product-item-purchase-quantity-' + index + '-' + subindex"
                        class="ml-1"
                        v-slot="{ errors }">
                        <d-input-group>
                          <input
                            type="number"
                            v-model="product.purchase_quantity"
                            min="0"
                            step="1"
                            :readonly="readOnly || !!product.order_product_deleted"
                            :class="{
                              'form-control': true,
                              'is-invalid': errors.length,
                              'text-right': true
                            }"
                            :invalid="errors.length ? true : null" />
                          <d-input-group-text slot="append">
                            {{ $t('ui.piecesAbbr') }}
                          </d-input-group-text>
                        </d-input-group>
                        <d-form-invalid-feedback>
                          {{ errors[0] }}
                        </d-form-invalid-feedback>
                      </validation-provider>

                      <d-button
                        v-if="!readOnly && (!product.order_product_deleted || product.returned_to_stock_at || product.collected === 0)"
                        :id="'remove-box-product-button-' + index + '-' + subindex"
                        theme="danger"
                        :outline="true"
                        class="ml-2 mr-2"
                        :disabled="removeInProgress === index + '-' + subindex"
                        @click.prevent.stop="removeProductFromBox(index, subindex)">
                        <div
                          v-if="removeInProgress === index + '-' + subindex"
                          class="spinner-border spinner-border-sm"
                          role="status"></div>
                        <i
                          v-else
                          class="material-icons">delete</i>
                      </d-button>

                      <d-button
                        v-if="product.order_product_deleted && !product.returned_to_stock_at && product.collected > 0"
                        :id="'return-to-stock-box-product-button-' + index + '-' + subindex"
                        theme="danger"
                        :outline="true"
                        class="ml-2 mr-2"
                        :disabled="!!product.return_to_stock"
                        @click.prevent.stop="returnToStockProductFromBox(index, subindex)"
                        :title="product.return_to_stock ? $t('order.warehouse.boxes.returnInProgress') : $t('order.returnToStock')">
                        <i
                          v-if="!product.return_to_stock"
                          class="material-icons">
                          undo
                        </i>
                        <i
                          v-else
                          class="material-icons">
                          published_with_changes
                        </i>
                      </d-button>
                    </d-col>
                  </template>
                </d-row>
              </d-card-body>
              <d-card-footer class="px-3 py-2 d-flex align-items-center">
                <small :class="{
                  'text-success': boxSpaceUsage[index] >= 0.9 && boxSpaceUsage[index] <= 1.0,
                  'text-danger': boxSpaceUsage[index] > 1.0,
                  'text-warning': boxSpaceUsage[index] < 0.9,
                }">
                  {{ $t('order.warehouse.boxes.fillLevel') }}:
                  <span v-if="boxSpaceUsage[index]">
                    {{ Number(boxSpaceUsage[index] * 100).toFixed(2) }}%
                  </span>
                  <span v-else>
                    &mdash; &mdash;
                  </span>
                </small>

                <small>
                  <a
                    v-if="readOnly"
                    href="#"
                    class="ml-3"
                    @click.prevent="showHistoryPopup('statuses', box.statuses)">
                    {{ $t('order.warehouse.boxes.statusHistory') }} ({{ box.statuses.length }})
                  </a>

                  <a
                    v-if="readOnly"
                    href="#"
                    class="ml-3 mr-3"
                    @click.prevent="showHistoryPopup('logs', box.logs)">
                    {{ $t('order.warehouse.boxes.changesHistory') }} ({{ box.logs.length }})
                  </a>
                </small>

                <d-button
                  v-if="readOnly && mode !== 'order'"
                  size="sm"
                  theme="primary"
                  :outline="true"
                  class="ml-auto"
                  :disabled="!!downloadingLabel"
                  @click.prevent="downloadLabel(box.id)">
                  <div
                    v-if="downloadingLabel === box.id"
                    class="spinner-border spinner-border-sm"
                    role="status"></div>
                  {{ $t('order.warehouse.boxes.downloadLabel') }}
                </d-button>

                <d-button
                  v-if="!readOnly && mode !== 'order'"
                  size="sm"
                  theme="success"
                  :outline="true"
                  class="ml-auto"
                  @click.prevent="addProductToBox(index)">
                  {{ $t('order.warehouse.boxes.addProduct') }}
                </d-button>

                <d-button
                  v-if="!readOnly && mode !== 'order'"
                  :id="'remove-box-button-' + index"
                  size="sm"
                  theme="danger"
                  :outline="true"
                  class="ml-2"
                  :disabled="box.products.filter(product => product.order_product_deleted).length > 0"
                  @click.prevent.stop="removeBox(index)">
                  {{ $t('order.warehouse.boxes.removeBox') }}
                </d-button>
              </d-card-footer>
            </d-card>
          </d-col>
          <d-col cols="6">
            <div style="position: sticky; top: 10px;" id="boxes-summary">
              <d-alert
                v-if="cheatSheetHasErrors"
                show
                theme="warning">
                {{ $t('order.warehouse.boxes.boxesWarning') }}
              </d-alert>
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th colspan="4">
                      {{ $t('order.warehouse.boxes.cheatsheet.status') }}
                    </th>
                  </tr>
                  <tr>
                    <th>{{ $t('order.warehouse.boxes.cheatsheet.productName') }}</th>
                    <th style="width: 110px;">{{ $t('order.warehouse.boxes.cheatsheet.toDivide') }}</th>
                    <th style="width: 110px;">{{ $t('order.warehouse.boxes.cheatsheet.divided') }}</th>
                    <th style="width: 140px;">{{ $t('order.warehouse.boxes.cheatsheet.fromSupplier') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(product, index) of cheatSheetForProducts"
                    :key="'cheatsheet-product-' + index"
                    :class="{
                      'is-invalid': productsCollected[product.order_product_id] > productsInBoxes[product.order_product_id] || productsInBoxes[product.order_product_id] !== product.quantity || productsCollected[product.order_product_id] > product.quantity_collected || productsFromSupplier[product.order_product_id] > product.purchase_quantity
                    }">
                    <td
                      :id="'cheatsheet-product-wrapper-' + index"
                      class="d-flex">
                      <div
                        v-if="productsCollected[product.order_product_id] > productsInBoxes[product.order_product_id] || productsInBoxes[product.order_product_id] !== product.quantity || productsCollected[product.order_product_id] > product.quantity_collected"
                        class="text-danger pr-2"
                        :id="'cheatsheet-product-' + index">
                        <i class="material-icons" style="cursor: pointer;">error</i>

                        <d-tooltip
                          :target="'#cheatsheet-product-' + index"
                          :container="'#cheatsheet-product-wrapper-' + index">
                          <small v-if="productsCollected[product.order_product_id] > productsInBoxes[product.order_product_id]">
                            🛑 {{ $t('order.warehouse.boxes.productQtyError1') }}
                          </small>

                          <small v-if="productsInBoxes[product.order_product_id] < product.quantity">
                            🛑 {{ $t('order.warehouse.boxes.productQtyError2') }}
                          </small>

                          <small v-if="productsInBoxes[product.order_product_id] > product.quantity">
                            🛑 {{ $t('order.warehouse.boxes.productQtyError3') }}
                          </small>

                          <small v-if="productsCollected[product.order_product_id] > product.quantity_collected">
                            🛑 {{ $t('order.warehouse.boxes.productQtyError4') }}
                          </small>

                          <small v-if="productsFromSupplier[product.order_product_id] > product.purchase_quantity">
                            🛑 {{ $t('order.warehouse.boxes.productQtyError5') }}
                          </small>
                        </d-tooltip>
                      </div>
                      <div>
                        <small>{{ product.code }} - {{ product.name }} (#{{ product.order_product_id }})</small>
                        <div v-if="readOnly">
                          <small>
                            {{ $t('order.warehouse.boxes.cheatsheet.collected') }}:
                            <span
                              v-if="productsCollected[product.order_product_id] !== productsInBoxes[product.order_product_id]"
                              class="text-danger">
                              {{ productsCollected[product.order_product_id] }} {{ $t('ui.piecesAbbr') }}
                            </span>

                            <span
                              v-else
                              class="text-success">
                              {{ productsCollected[product.order_product_id] }} {{ $t('ui.piecesAbbr') }}
                            </span>
                            |
                            {{ $t('order.warehouse.boxes.cheatsheet.verified') }}:
                            <span
                              v-if="productsVerified[product.order_product_id] !== productsInBoxes[product.order_product_id]"
                              class="text-danger">
                              {{ productsVerified[product.order_product_id] }} {{ $t('ui.piecesAbbr') }}
                            </span>
                            <span
                              v-else
                              class="text-success">
                              {{ productsVerified[product.order_product_id] }} {{ $t('ui.piecesAbbr') }}
                            </span>
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>
                      {{ product.quantity }} {{ $t('ui.piecesAbbr') }}
                    </td>
                    <td>
                      <span
                        v-if="productsInBoxes[product.order_product_id] === product.quantity"
                        class="text-success">
                        {{ productsInBoxes[product.order_product_id] }} {{ $t('ui.piecesAbbr') }}
                      </span>
                      <span
                        v-else
                        class="text-danger">
                        {{ productsInBoxes[product.order_product_id] }} {{ $t('ui.piecesAbbr') }}
                      </span>
                    </td>
                    <td>
                      <span>
                        {{ product.purchase_quantity }} {{ $t('ui.piecesAbbr') }}<br>
                        <small>{{ $t('order.warehouse.boxes.cheatsheet.divided') }}: {{ productsFromSupplier[product.order_product_id] }} {{ $t('ui.piecesAbbr') }}</small>
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>

              <table class="table table-sm">
                <thead>
                  <tr>
                    <th colspan="3">{{ $t('order.warehouse.boxes.cheatsheet.title') }}</th>
                  </tr>
                  <tr>
                    <th>{{ $t('order.warehouse.boxes.cheatsheet.productName') }}</th>
                    <th style="width: 150px;">{{ $t('order.warehouse.boxes.cheatsheet.piecesInCarton') }}</th>
                    <th style="width: 150px;">{{ $t('order.warehouse.boxes.cheatsheet.realPiecesInCarton') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(product, index) of cheatSheetForProducts"
                    :key="'cheatsheet-product-' + index">
                    <td>
                      <small>{{ product.code }} - {{ product.name }} (#{{ product.order_product_id }})</small>
                    </td>
                    <td>
                      {{ product.pieces_in_carton }} {{ $t('ui.piecesAbbr') }}
                    </td>
                    <td>
                      {{ product.real_pieces_in_carton }} {{ $t('ui.piecesAbbr') }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </d-col>
        </validation-observer>
      </div>
    </div>

    <order-collect-boxes-history-popup
      v-if="displayHistoryPopup"
      :type="historyPopupDataType"
      :data="historyPopupDataData" />
  </d-card>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';
import OrderCollectBoxesHistoryPopup from './OrderCollectBoxesHistoryPopup.vue';
import Vue from 'vue';

export default {
  name: 'order-collect-boxes',
  props: [
    'deliveryNoteConfirmedAt',
    'deliveryNoteConfirmedBy',
    'boxesAcceptedAt',
    'orderID',
    'orderProducts',
    'mode'
  ],
  components: {
    'order-collect-boxes-history-popup': OrderCollectBoxesHistoryPopup
  },
  computed: {
    readOnly () {
      if (this.boxes.length) {
        return !!this.boxesAcceptedAt;
      }

      return false;
    },
    orderProductIDs () {
      return this.orderProducts.map(product => product.product_id);
    },
    showBoxInitButton () {
      return this.loadedBoxes && !this.loadError && !this.boxes.length;
    },
    purchaseQuantities () {
      let qtys = this.orderProducts.map(product => ({
        id: product.id,
        purchase_quantity: product.purchase_quantity
      }));
      let result = {};

      for (let i = 0; i < qtys.length; i++) {
        result[qtys[i].id] = qtys[i].purchase_quantity;
      }

      return result;
    },
    cheatSheetForProducts () {
      return this.orderProducts.map(product => ({
        order_product_id: product.id,
        code: product.code,
        name: product.name,
        quantity: product.quantity,
        purchase_quantity: product.purchase_quantity,
        pieces_in_carton: product.pieces_in_carton,
        real_pieces_in_carton: product.real_pieces_in_carton
      }));
    },
    orderProductsList () {
      return [{
        text: this.$t('product.chooseProduct'),
        value: null
      }].concat(this.orderProducts.map(product => ({
        text: product.code + ' - ' + product.name + ' (#' + product.id + ')',
        value: product.id
      })));
    },
    productsPiecesInCarton () {
      let piecesInCarton = {};

      for (let i = 0; i < this.orderProducts.length; i++) {
        let id = this.orderProducts[i].id;
        piecesInCarton[id] = this.orderProducts[i].pieces_in_carton;
      }

      return piecesInCarton;
    },
    boxSpaceUsage () {
      let usage = [];

      for (let i = 0; i < this.boxes.length; i++) {
        usage[i] = this.boxes[i].products.reduce((prev, next) => {
          if (next.order_product && next.order_product.pieces_in_carton) {
            return prev + (next.box_quantity / next.order_product.pieces_in_carton);
          }

          return prev + (next.box_quantity / this.productsPiecesInCarton[next.order_product_id]);
        }, 0);
      }

      return usage;
    },
    productsInBoxes () {
      let inBoxes = {};

      for (let i = 0; i < this.orderProducts.length; i++) {
        let id = this.orderProducts[i].id;
        inBoxes[id] = 0;

        for (let j = 0; j < this.boxes.length; j++) {
          for (let k = 0; k < this.boxes[j].products.length; k++) {
            if (id === this.boxes[j].products[k].order_product_id) {
              inBoxes[id] += +this.boxes[j].products[k].box_quantity;
            }
          }
        }
      }

      return inBoxes;
    },
    productsInOrder () {
      let inOrder = {};

      for (let i = 0; i < this.orderProducts.length; i++) {
        let id = this.orderProducts[i].id;
        inOrder[id] = this.orderProducts[i].quantity;
      }

      return inOrder;
    },
    productsVerified () {
      let inBoxes = {};

      for (let i = 0; i < this.orderProducts.length; i++) {
        let id = this.orderProducts[i].id;
        inBoxes[id] = 0;

        for (let j = 0; j < this.boxes.length; j++) {
          for (let k = 0; k < this.boxes[j].products.length; k++) {
            if (id === this.boxes[j].products[k].order_product_id) {
              inBoxes[id] += +this.boxes[j].products[k].verified;
            }
          }
        }
      }

      return inBoxes;
    },
    productsCollected () {
      let inBoxes = {};

      for (let i = 0; i < this.orderProducts.length; i++) {
        let id = this.orderProducts[i].id;
        inBoxes[id] = 0;

        for (let j = 0; j < this.boxes.length; j++) {
          for (let k = 0; k < this.boxes[j].products.length; k++) {
            if (id === this.boxes[j].products[k].order_product_id) {
              inBoxes[id] += +this.boxes[j].products[k].collected;
            }
          }
        }
      }

      return inBoxes;
    },
    productsFromSupplier () {
      let inBoxes = {};

      for (let i = 0; i < this.orderProducts.length; i++) {
        let id = this.orderProducts[i].id;
        inBoxes[id] = 0;

        for (let j = 0; j < this.boxes.length; j++) {
          for (let k = 0; k < this.boxes[j].products.length; k++) {
            if (id === this.boxes[j].products[k].order_product_id) {
              inBoxes[id] += +this.boxes[j].products[k].purchase_quantity;
            }
          }
        }
      }

      return inBoxes;
    },
    cheatSheetHasErrors () {
      if (!this.boxesAcceptedAt) {
        return false;
      }

      for (let i = 0; i < this.cheatSheetForProducts.length; i++) {
        let product = this.cheatSheetForProducts[i];

        if (
          this.productsCollected[product.order_product_id] > this.productsInBoxes[product.order_product_id] ||
          this.productsInBoxes[product.order_product_id] !== product.quantity ||
          this.productsCollected[product.order_product_id] > product.quantity_collected
        ) {
          return true;
        }
      }

      for (let i = 0; i < this.boxes.length; i++) {
        let box = this.boxes[i];

        for (let j = 0; j < box.products.length; j++) {
          let product = box.products[j];

          if (this.orderProductIDs.indexOf(product.product_id) === -1) {
            return true;
          }
        }
      }

      return false;
    }
  },
  data () {
    return {
      boxes: [],
      // flags
      downloadingLabel: false,
      loadedBoxes: false,
      loadError: false,
      hasSavedBoxes: false,
      cancelInProgress: false,
      saveInProgress: false,
      removeInProgress: false,
      // history popup
      displayHistoryPopup: false,
      historyPopupDataData: null,
      historyPopupDataType: null
    };
  },
  mounted () {
    this.loadBoxes();
    this.$bus.$on('close-order-collect-boxes-history-popup', this.closeHistoryPopup);
  },
  methods: {
    loadBoxes () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/orders/boxes/items',
        loadedKey: 'loadedBoxes',
        outputKey: 'boxes',
        errorKey: 'loadError',
        params: {
          where: {
            order_id: this.orderID
          }
        },
        successAction: (response) => {
          if (response.length) {
            this.hasSavedBoxes = true;

            for (let i = 0; i < response.length; i++) {
              if (response[i].embroidery_received_comment) {
                this.$bus.$emit('show-order-embroidery-alert');
                break;
              }
            }

            for (let i = 0; i < this.cheatSheetForProducts.length; i++) {
              let product = this.cheatSheetForProducts[i];

              if (this.productsInOrder[product.order_product_id] !== this.productsInBoxes[product.order_product_id]) {
                this.$bus.$emit('show-order-boxes-alert');
              }
            }
          }
        }
      });
    },
    generateBoxes () {
      Vue.set(this, 'boxes', []);

      let productsToDivide = this.orderProducts.map(product => ({
        name: product.name,
        order_product_id: product.id,
        pieces_in_carton: product.pieces_in_carton,
        real_pieces_in_carton: product.real_pieces_in_carton,
        quantity: product.quantity,
        quantityToDivide: product.quantity
      }));

      for (let i = 0; i < productsToDivide.length; i++) {
        let qty = productsToDivide[i].quantity;
        let piecesInCarton = productsToDivide[i].pieces_in_carton;

        if (piecesInCarton > 0) {
          let requiredFullBoxes = Math.floor(qty / piecesInCarton);
          let leftItems = (qty - (piecesInCarton * requiredFullBoxes));

          if (requiredFullBoxes >= 1) {
            this.addFullBoxes(requiredFullBoxes, productsToDivide[i]);
          }

          if (leftItems > 0) {
            this.addPartialBoxes(leftItems, productsToDivide[i]);
          }
        }
      }

      if (this.boxes.length === 0) {
        this.boxes.push({
          products: [{
            order_product_id: null,
            box_quantity: 0,
            purchase_quantity: 0
          }]
        });
      }
    },
    addFullBoxes (amount, product) {
      for (let i = 0; i < amount; i++) {
        this.boxes.push({
          products: [{
            order_product_id: product.order_product_id,
            box_quantity: product.pieces_in_carton,
            purchase_quantity: 0
          }]
        });
      }
    },
    addPartialBoxes (amountToPut, product) {
      for (let i = 0; i < this.boxes.length; i++) {
        let boxFillLevel = this.boxes[i].products.reduce((prev, next) => prev + (next.box_quantity / this.productsPiecesInCarton[next.order_product_id]), 0);

        if (boxFillLevel < 1) {
          let productItemFill = 1 / this.productsPiecesInCarton[product.order_product_id];
          let availableFill = 1 - boxFillLevel;
          let itemsToPut = Math.floor(availableFill / productItemFill);

          if (itemsToPut >= amountToPut) {
            this.boxes[i].products.push({
              order_product_id: product.order_product_id,
              box_quantity: amountToPut,
              purchase_quantity: 0
            });
            amountToPut = 0;
            break;
          } else {
            this.boxes[i].products.push({
              order_product_id: product.order_product_id,
              box_quantity: itemsToPut,
              purchase_quantity: 0
            });
            amountToPut -= itemsToPut;
          }
        }
      }

      if (amountToPut > 0) {
        this.boxes.push({
          products: [{
            order_product_id: product.order_product_id,
            box_quantity: amountToPut,
            purchase_quantity: 0
          }]
        });
      }
    },
    addBox () {
      this.boxes.unshift({
        products: [{
          order_product_id: null,
          box_quantity: 0,
          purchase_quantity: 0
        }]
      });
    },
    addProductToBox (boxIndex) {
      this.boxes[boxIndex].products.push({
        order_product_id: null,
        box_quantity: 0,
        purchase_quantity: 0
      });
    },
    removeBox (boxIndex) {
      this.boxes.splice(boxIndex, 1);
    },
    removeProductFromBox (boxIndex, productIndex) {
      this.boxes[boxIndex].products.splice(productIndex, 1);

      Vue.nextTick(() => {
        if (this.boxes[boxIndex].products.length === 0) {
          this.boxes.splice(boxIndex, 1);
        }
      });
    },
    returnToStockProductFromBox (boxIndex, productIndex, returnedToStock) {
      Vue.set(this.boxes[boxIndex].products[productIndex], 'return_to_stock', 1);

      if (returnedToStock) {
        this.removeInProgress = boxIndex + '-' + productIndex;

        FormUtils.submitInBackground({
          $http: this.$http,
          endpoint: '/orders/boxes/remove-product/' + this.boxes[boxIndex].products[productIndex].id,
          errorTitle: this.$t('ui.errorOccured'),
          errorText: this.$t('ui.actionFailedTryAgain'),
          successAction: () => {
            this.removeInProgress = false;

            Vue.swal({
              title: this.$t('order.warehouse.boxes.removeBoxProductSuccessTitle'),
              html: this.$t('order.warehouse.boxes.removeBoxProductSuccessDesc'),
              type: 'success',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });

            this.boxes[boxIndex].products.splice(productIndex, 1);

            Vue.nextTick(() => {
              if (this.boxes[boxIndex].products.length === 0) {
                this.boxes.splice(boxIndex, 1);
              }
            });
          },
          failAction: () => {
            this.removeInProgress = false;
          }
        });
      }
    },
    resetBoxes () {
      Vue.swal({
        title: this.$t('order.warehouse.boxes.resetTitle'),
        text: this.$t('order.warehouse.boxes.resetText'),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('order.warehouse.boxes.yesReset'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value) {
          this.generateBoxes();
        }
      });
    },
    saveBoxes () {
      for (let i = 0; i < this.cheatSheetForProducts.length; i++) {
        let product = this.cheatSheetForProducts[i];

        if (this.productsInBoxes[product.order_product_id] > product.quantity) {
          Vue.swal({
            title: this.$t('order.warehouse.boxes.productsTooMuchTitle'),
            html: this.$t('order.warehouse.boxes.productsTooMuchDesc'),
            type: 'error',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
          return;
        }
      }

      FormUtils.validate(this.$refs['boxes-divide-form'], this.saveBoxesAction);
    },
    saveBoxesAction () {
      this.saveInProgress = true;
      let preparedBoxes = JSON.parse(JSON.stringify(this.boxes));
      preparedBoxes = preparedBoxes.map(box => ({
        id: box.id,
        products: box.products.map(product => ({
          id: product.id,
          order_product_id: product.order_product_id,
          box_quantity: product.box_quantity,
          purchase_quantity: parseInt(product.purchase_quantity, 10),
          return_to_stock: product.return_to_stock
        }))
      }));

      Vue.swal({
        title: this.$t('order.warehouse.boxes.acceptTitle'),
        text: this.$t('order.warehouse.boxes.acceptDesc'),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('ui.yes'),
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value) {
          FormUtils.submitInBackground({
            $http: this.$http,
            endpoint: '/orders/boxes/store',
            errorTitle: this.$t('ui.errorOccured'),
            errorText: this.$t('ui.actionFailedTryAgain'),
            formData: {
              order_id: this.orderID,
              boxes: preparedBoxes
            },
            successAction: () => {
              this.saveInProgress = false;
              this.$bus.$emit('order-form-collect-reload-boxes-accepted-status');
              this.loadedBoxes = false;
              this.loadBoxes();

              Vue.swal({
                title: this.$t('order.warehouse.boxes.saveSuccessTitle'),
                html: this.$t('order.warehouse.boxes.saveSuccessDesc'),
                type: 'success',
                confirmButtonText: this.$t('ui.ok'),
                buttonsStyling: true
              });
            },
            failAction: () => {
              this.saveInProgress = false;
            }
          });
        } else {
          this.saveInProgress = false;
        }
      });
    },
    cancelBoxes () {
      this.cancelInProgress = true;

      Vue.swal({
        title: this.$t('order.warehouse.boxes.cancelTitle'),
        text: this.$t('order.warehouse.boxes.cancelDesc'),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'OK',
        cancelButtonText: this.$t('ui.cancel')
      }).then((result) => {
        if (result.value) {
          FormUtils.submitInBackground({
            $http: this.$http,
            endpoint: '/orders/boxes/cancel-accept',
            errorTitle: this.$t('ui.errorOccured'),
            errorText: this.$t('ui.actionFailedTryAgain'),
            formData: {
              order_id: this.orderID
            },
            successAction: () => {
              this.cancelInProgress = false;
              this.$bus.$emit('order-form-collect-reload-boxes-accepted-status');
              this.loadedBoxes = false;
              this.loadBoxes();
            },
            failAction: () => {
              this.cancelInProgress = false;
            }
          });
        } else {
          this.cancelInProgress = false;
        }
      });
    },
    downloadLabel (boxID) {
      this.downloadingLabel = boxID;
      let endpointUrl = '/orders/boxes/download-box-label/' + boxID;

      if (boxID === 'all') {
        endpointUrl = '/orders/boxes/download-order-labels/' + this.orderID;
      }

      this.$http.post(endpointUrl, {}, {
        responseType: 'blob'
      }).then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        document.body.appendChild(link);
        let filename = response.request.getResponseHeader('content-disposition').split('filename=')[1].split(';')[0];
        link.setAttribute('download', filename.replace(/"/gmi, ''));
        link.click();
        document.body.removeChild(link);
        this.downloadingLabel = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccuredDuringDownload'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.downloadingLabel = false;
      });
    },
    showHistoryPopup (type, data) {
      this.historyPopupDataType = type;
      this.historyPopupDataData = data;

      Vue.nextTick(() => {
        this.displayHistoryPopup = true;
      });
    },
    closeHistoryPopup () {
      this.displayHistoryPopup = false;
      this.historyPopupDataType = null;
      this.historyPopupDataData = null;
    }
  },
  beforeDestroy () {
    this.$bus.$off('close-order-collect-boxes-history-popup', this.closeHistoryPopup);
  }
}
</script>

<style lang="scss">
.order-collect-boxes {
  .card-body.border-danger {
    border: 2px solid #f33;
    border-radius: 10px;
    color: #f33;
  }

  .card-footer {
    background: rgba(0, 0, 0, 0.03);
  }

  .is-loading {
    opacity: .25;
    pointer-events: none;
  }

  .product-stats {
    display: grid;
    grid-template-columns: 150px 150px calc(100% - 300px);
    grid-column-gap: 20px;
    padding-top: 5px;

    small {
      white-space: nowrap;
    }
  }

  .box-info {
    & > small:not(.is-embroidery-comment) {
      border: 1px solid #ccc;
      border-radius: 5px;
      display: flex;
      flex-wrap: wrap;
    }

    .is-embroidery-comment {
      background: red;
      border-radius: 5px;
      color: #fff;
    }
  }

  .is-invalid {
    background: rgba(255, 0, 0, .125);

    td {
      background: rgba(255, 0, 0, .125);
    }
  }
}
</style>
